// Everyday objects (24 Oct 2026, Year 1): one plain drawing of a thing from
// home or school, such as a tin can or a dice, to sit beside the `solid_3d`
// drawings. It is the question's picture ("What can this tin do?") or one
// of the picture options ("Which thing has the same shape?"). Each is drawn
// on the same slant as the solids, so its ball shape, can shape or box shape
// shows. No word is written on the picture.
//
// Pure parsing only, no components (see practice-diagram-data.ts).

export const EVERYDAY_OBJECTS = [
  'tennis_ball',
  'orange',
  'tin_can',
  'tissue_box',
  'cereal_box',
  'dice',
  'party_hat',
  'ice_cream_cone',
  'building_block',
] as const;

export type EverydayObject = (typeof EVERYDAY_OBJECTS)[number];

export type EverydayObjectSpec = {
  type: 'everyday_object';
  object: EverydayObject;
};

export function readEverydayObject(
  record: Record<string, unknown>,
): EverydayObjectSpec | null {
  const object = EVERYDAY_OBJECTS.find((known) => known === record.object);
  return object ? { type: 'everyday_object', object } : null;
}

const WORDS: Record<EverydayObject, string> = {
  tennis_ball: 'a tennis ball',
  orange: 'an orange',
  tin_can: 'a tin can',
  tissue_box: 'a tissue box',
  cereal_box: 'a cereal box',
  dice: 'a dice',
  party_hat: 'a party hat',
  ice_cream_cone: 'an ice-cream cone',
  building_block: 'a building block',
};

// What a screen reader is told in place of the picture: what the thing is,
// as a child seeing it would say. Never its shape, whether it rolls or
// stacks, or which solid it matches: those are the questions.
export function describeEverydayObject(spec: EverydayObjectSpec): string {
  return `A drawing of ${WORDS[spec.object]}.`;
}

// The drawing's box. The units are close to one pixel each when the
// picture is shown at its natural width.
export const EVERYDAY_OBJECT_WIDTH = 160;
export const EVERYDAY_OBJECT_HEIGHT = 140;
